import { useState } from 'react';
import { Chip } from '@jotpsych/ui';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';

interface SuggestedSectionProps {
  /** How many suggested items the section holds; 0 renders nothing. */
  count: number;
  /** Noun for the aria labels, e.g. "medications". */
  itemNoun: string;
  /** Suggestions can run long — the section starts folded by default. */
  defaultExpanded?: boolean;
  /** Notification for the host's analytics — fired after the caret toggles. */
  onSectionToggled?: (expanded: boolean) => void;
  /** Caps the expanded body with internal scroll. */
  contentMaxHeight?: number | string;
  children: React.ReactNode;
}

/**
 * Collapsible home for AI chart-proposals (GDN-2472): confirmed chart data
 * leads the card, and the review queue folds beneath it with a count badge —
 * mirroring the medication-history section pattern. Valcourt's measured
 * backlog (thousands of pending proposals) is why these don't render inline.
 */
export const SuggestedSection: React.FC<SuggestedSectionProps> = ({
  count,
  itemNoun,
  defaultExpanded = false,
  onSectionToggled,
  contentMaxHeight,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (count === 0) return null;

  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    onSectionToggled?.(next);
  };

  return (
    <Box sx={{ borderTop: '1px solid', borderColor: 'divider', px: 2, py: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        onClick={toggle}
        sx={{ alignItems: 'center', cursor: 'pointer' }}
      >
        <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Suggested
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ flex: 1 }}>
          extracted from notes · needs review
        </Typography>
        <Chip
          variant="soft"
          size="xSmall"
          color="info"
          label={`${count} suggested`}
        />
        <IconButton
          size="small"
          aria-label={
            expanded
              ? `Collapse suggested ${itemNoun}`
              : `Expand suggested ${itemNoun}`
          }
          aria-expanded={expanded}
          onClick={(clickEvent) => {
            clickEvent.stopPropagation();
            toggle();
          }}
        >
          {expanded ? (
            <ExpandLessIcon fontSize="small" />
          ) : (
            <ExpandMoreIcon fontSize="small" />
          )}
        </IconButton>
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box
          sx={
            contentMaxHeight
              ? { maxHeight: contentMaxHeight, overflowY: 'auto' }
              : undefined
          }
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};
